import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import useAuth from "../hooks/useAuth";
import Home from "../pages/Home";
import Signin from "../pages/Signin";
import Signup from "../pages/Signup";
import ForgotPassword from "../pages/ForgotPassword";
import EmailVerification from "../pages/Verification";
import Profile from "../pages/Profile";
import ResetPassword from "../pages/ResetPassword";
import Enterprise from "../pages/Enterprise";
import Leadconnector from "../pages/Leadconnector";

const AuthenticatedLayout = ({ children }) => <>{children}</>;

const PublicLayout = ({ children }) => (
  <div className="public-container">{children}</div>
);

const LoadingScreen = ({ message = "Carregando...", showSpinner = true }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    width: '100vw',
    background: '#f9fafb',
    gap: '1.5rem',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 9999
  }}>
    {showSpinner && (
      <div style={{
        width: '48px',
        height: '48px',
        border: '4px solid rgba(62, 111, 92, 0.2)',
        borderTop: '4px solid #3E6F5C',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
    )}
    <p style={{
      color: '#3E6F5C',
      fontSize: '1rem',
      fontWeight: 600,
      margin: 0
    }}>{message}</p>
  </div>
);

const PrivateRoute = ({ children }) => {
  const { signed, loading, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !signed) {
      navigate("/", { replace: true });
    }
  }, [signed, loading, navigate]);

  // Redirecionamento automático para usuários do Enterprise
  useEffect(() => {
    if (!loading && signed && user && user.enterprise_id) {
      const currentPath = window.location.pathname;
      const enterprisePath = `/enterprise/${user.enterprise_id}`;

      // Se o usuário é Normal e não está na página enterprise correta, redireciona
      if (user.enterprise_role === "Normal" && currentPath !== enterprisePath) {
        navigate(enterprisePath, { replace: true });
      }

      // Se o usuário está em uma página enterprise que não pertence a ele, verifica permissões
      if (
        currentPath.startsWith("/enterprise/") &&
        user.enterprise_role === "Normal"
      ) {
        const pathEnterpriseId = currentPath.split("/")[2];
        if (pathEnterpriseId !== user.enterprise_id) {
          navigate(enterprisePath, { replace: true });
        }
      }
    }
  }, [signed, loading, user, navigate]);

  // Aguarda o carregamento completo antes de renderizar
  if (loading) {
    return <LoadingScreen message="Carregando..." showSpinner={true} />;
  }

  // Se não está autenticado, não renderiza nada (será redirecionado pelo useEffect)
  if (!signed) {
    return null;
  }

  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
};

const PublicRoute = ({ children }) => {
  const { signed, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && signed) {
      navigate("/home", { replace: true });
    }
  }, [signed, loading, navigate]);

  if (loading) {
    return <LoadingScreen message="Carregando..." showSpinner={true} />;
  }

  if (signed) {
    return null;
  }

  return <PublicLayout>{children}</PublicLayout>;
};

const RoutesApp = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicRoute>
            <Signin />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/email-verification"
        element={
          <PublicRoute>
            <EmailVerification />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/leadconnector" element={<Leadconnector />} />

      <Route
        path="/home"
        element={
          <PrivateRoute>
            <Home />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      <Route
        path="/enterprise/:id"
        element={
          <PrivateRoute>
            <Enterprise />
          </PrivateRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default RoutesApp;
